/**
 * Entscheidet, wann ein Tab die neue Version lädt. Reine Logik ohne Browser-Objekte — die
 * Verdrahtung mit Service Worker und Dokument steht in ./index.ts.
 *
 * Ablauf: Ein neuer Service Worker ist installiert und wartet (updateFound). Bei jedem sicheren
 * Moment (trigger) prüft der Controller nach kurzer Wartezeit, ob gerade etwas in Arbeit ist und ob
 * der Server antwortet. Wenn ja, bekommt der wartende Worker SKIP_WAITING; sobald er übernimmt
 * (controllerChanged), wird neu geladen — nach einer letzten Prüfung, denn dazwischen kann jemand
 * zu tippen begonnen haben.
 */
import type { AppUpdateBusyReason } from './guards';

export type AppUpdateTrigger = 'navigation' | 'hidden' | 'idle';

export interface AppUpdateEnvironment {
  /** Was gerade gegen einen Reload spricht — null, wenn nichts. */
  busyReason(): AppUpdateBusyReason | null;
  isHidden(): boolean;
  /**
   * Antwortet der Server? Ein Reload, während er nach einem Deploy noch hochfährt, zeigt nur
   * Fehler — bis v1.354.2 sogar die Anmeldeseite (stores/auth.ts).
   */
  serverReachable(): Promise<boolean>;
  /** SKIP_WAITING an den wartenden Worker. false, wenn keiner (mehr) wartet. */
  activateWaitingWorker(): boolean;
  reload(): void;
  log(message: string): void;
  setTimeout(callback: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

export const TRIGGER_DELAY_MS: Record<AppUpdateTrigger, number> = {
  // Schließende Dialoge (Mantine-Transition) und das letzte Sichern der verlassenen Seite sollen
  // schon sichtbar sein, bevor geprüft wird.
  navigation: 400,
  // Servicebericht & Co. sichern beim Wechsel in den Hintergrund selbst — ihr Handler läuft nach
  // diesem, die Anfrage zählt also erst einen Moment später.
  hidden: 1000,
  idle: 0,
};

/** Laufende Schreibanfrage: so oft und so lange abwarten, dann bis zum nächsten Anlass aufgeben. */
export const WRITE_RETRY_MS = 500;
export const WRITE_RETRY_MAX = 20;

/** Bleibt der Controller-Wechsel nach SKIP_WAITING aus (Worker inzwischen überholt): trotzdem neu laden. */
export const ACTIVATION_TIMEOUT_MS = 5000;

type Blocker = AppUpdateBusyReason | 'tab-visible' | 'server-unavailable';

export function createAppUpdateController(env: AppUpdateEnvironment) {
  let updateReady = false;
  let reloading = false;
  let checkingServer = false;
  let scheduled: unknown = null;
  let activation: { trigger: AppUpdateTrigger; timeout: unknown } | null = null;
  let lastLoggedBlocker: Blocker | null = null;

  const idle = () => updateReady && !reloading && !activation && !checkingServer;

  const blocker = (trigger: AppUpdateTrigger): Blocker | null => {
    // Wer inzwischen zurück im Tab ist, soll die Seite nicht unter den Augen neu laden sehen.
    if (trigger === 'hidden' && !env.isHidden()) return 'tab-visible';
    return env.busyReason();
  };

  const logBlocked = (reason: Blocker) => {
    if (reason === lastLoggedBlocker) return;
    lastLoggedBlocker = reason;
    env.log(`Neue Version wartet — nicht neu geladen (${reason}).`);
  };

  const reloadIfStillSafe = (trigger: AppUpdateTrigger) => {
    if (reloading) return;
    const reason = blocker(trigger);
    if (reason) {
      logBlocked(reason);
      return;
    }
    reloading = true;
    env.log(`Lade neue Version (${trigger}).`);
    env.reload();
  };

  const activateOrReload = (trigger: AppUpdateTrigger) => {
    if (env.activateWaitingWorker()) {
      const timeout = env.setTimeout(() => {
        activation = null;
        reloadIfStillSafe(trigger);
      }, ACTIVATION_TIMEOUT_MS);
      activation = { trigger, timeout };
      return;
    }
    // Kein wartender Worker mehr — ein anderer Tab hat ihn schon aktiviert, nur diese Seite ist alt.
    reloadIfStillSafe(trigger);
  };

  const attempt = (trigger: AppUpdateTrigger, retries: number) => {
    scheduled = null;
    if (!idle()) return;
    const reason = blocker(trigger);
    if (reason === 'pending-writes' && retries < WRITE_RETRY_MAX) {
      scheduled = env.setTimeout(() => attempt(trigger, retries + 1), WRITE_RETRY_MS);
      return;
    }
    if (reason) {
      logBlocked(reason);
      return;
    }
    checkingServer = true;
    void env
      .serverReachable()
      .catch(() => false)
      .then((reachable) => {
        checkingServer = false;
        if (!updateReady || reloading || activation) return;
        if (!reachable) {
          logBlocked('server-unavailable');
          return;
        }
        // Die Abfrage dauert einen Moment — so lange kann jemand angefangen haben zu tippen.
        const again = blocker(trigger);
        if (again) {
          logBlocked(again);
          return;
        }
        activateOrReload(trigger);
      });
  };

  return {
    /** Ein neuer Service Worker ist installiert und wartet. */
    updateFound(): void {
      if (!updateReady) env.log('Neue Version bereit — wird im nächsten sicheren Moment geladen.');
      updateReady = true;
    },

    /**
     * Der Service Worker dieser Seite hat gewechselt. Nicht bei der Erst-Installation aufrufen —
     * eine vorher unkontrollierte Seite ist nicht veraltet.
     */
    controllerChanged(): void {
      updateReady = true;
      // Ein anderer Tab hat aktiviert: Hier entscheidet der eigene nächste sichere Moment.
      if (!activation) return;
      env.clearTimeout(activation.timeout);
      const { trigger } = activation;
      activation = null;
      reloadIfStillSafe(trigger);
    },

    /** Sicherer Moment — geprüft wird nach TRIGGER_DELAY_MS. */
    trigger(trigger: AppUpdateTrigger): void {
      if (!idle()) return;
      if (scheduled !== null) env.clearTimeout(scheduled);
      scheduled = env.setTimeout(() => attempt(trigger, 0), TRIGGER_DELAY_MS[trigger]);
    },

    isUpdateReady(): boolean {
      return updateReady;
    },
  };
}

export type AppUpdateController = ReturnType<typeof createAppUpdateController>;
